/**
 * What a row on the board is.
 *
 * The board used to be a grid of people, so every drop target, every preview
 * placement and every registered rectangle was a `userId`. It is now three
 * kinds of row — a person, the unassigned queue, and a parking bucket — and
 * the gesture machine has to be able to say "the pointer is over *that* one"
 * without caring which kind it is.
 *
 * The identifier is a tagged string rather than an object so it can be a `Map`
 * key and a `data-` attribute without any ceremony. `user:` is prefixed rather
 * than bare so that a raw user id can never be mistaken for a resource id by a
 * function that was missed in the conversion — the two are different types and
 * the wrong one fails to parse rather than silently addressing the wrong row.
 *
 * Note that "lane" is not the word for any of this. `packLanes` already means
 * the overlap columns *within* one row, and that meaning predates these.
 */

export type ResourceId = string

export type BoardResource =
  | { kind: 'user'; userId: string }
  | { kind: 'unassigned' }
  | { kind: 'bucket'; bucketId: string }

/** The row for a person. */
export function userResource(userId: string): ResourceId {
  return `user:${userId}`
}

/** The row holding booked work with nobody on it yet. */
export const UNASSIGNED_RESOURCE: ResourceId = 'unassigned'

/** The row for a named parking bucket. */
export function bucketResource(bucketId: string): ResourceId {
  return `bucket:${bucketId}`
}

export function parseResource(id: ResourceId): BoardResource | null {
  if (id === UNASSIGNED_RESOURCE) return { kind: 'unassigned' }
  if (id.startsWith('user:')) return { kind: 'user', userId: id.slice(5) }
  if (id.startsWith('bucket:')) return { kind: 'bucket', bucketId: id.slice(7) }
  return null
}

/** The user id behind a resource, or null when the row is not a person. */
export function userIdOf(id: ResourceId): string | null {
  const resource = parseResource(id)
  return resource?.kind === 'user' ? resource.userId : null
}

/** The bucket id behind a resource, or null when the row is not a bucket. */
export function bucketIdOf(id: ResourceId): string | null {
  const resource = parseResource(id)
  return resource?.kind === 'bucket' ? resource.bucketId : null
}

export function isUserResource(id: ResourceId): boolean {
  return userIdOf(id) !== null
}

/**
 * Which row a visit currently belongs to.
 *
 * A visit with assignments belongs to the first assignee's row — the same
 * choice the board has always made, and the reason a two-person job draws in
 * both columns is that the *views* group by every assignment while a *drag*
 * has to pick one origin. A visit with no assignments at all is a real
 * booking nobody has been put on yet, and it lives on the unassigned row.
 */
export function resourceOfVisit(visit: {
  assignments: Array<{ userId: string }>
}): ResourceId {
  const first = visit.assignments[0]?.userId
  return first ? userResource(first) : UNASSIGNED_RESOURCE
}
